import { yupResolver } from '@hookform/resolvers/yup'
import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import * as yup from 'yup'

import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { AcknowledgeModal, ActionModal, Flex, FormField, Modal, Select, Typography } from '@percent/lemonade'
import { useMutation } from '@percent/utility'
import { AgentVerificationMarkDocAsFraudModalProps } from './AgentVerificationMarkDocAsFraudModal.types'
import { DocumentFraudCode, markDocAsFraudReasons } from '@percent/admin-dashboard/api/types'

export function AgentVerificationMarkDocAsFraudModal({
  isMarkAsFraudModalOpened,
  toggleMarkAsFraudModal,
  selectedDocument,
  refresh
}: AgentVerificationMarkDocAsFraudModalProps) {
  const { t } = useTranslation()
  const { adminService } = useServices()
  const [markAsFraudStatus, setMarkAsFraudStatus] = useState<'success' | 'error'>()

  const { handleSubmit, setValue, watch } = useForm<{ reasonCode: DocumentFraudCode }>({
    resolver: yupResolver(
      yup.object().shape({
        reasonCode: yup
          .string()
          .oneOf([...markDocAsFraudReasons])
          .required(t('form.required'))
      })
    )
  })

  const [{ isPending: isMarkAsFraudLoading, errorMessage }, { apiFunc: markAsFraud }] = useMutation(
    adminService.requestMarkDocumentAsFraud,
    () => {
      setMarkAsFraudStatus('success')
      refresh()
    },
    () => setMarkAsFraudStatus('error')
  )

  const options = useMemo(() => {
    return markDocAsFraudReasons.map(reason => ({
      label: t(`dropdown.markDocAsFraud.${reason}`),
      value: reason
    }))
  }, [t])

  const handleMarkAsFraud = handleSubmit(({ reasonCode }) => {
    if (!selectedDocument?.id) return

    markAsFraud({ id: selectedDocument.id, payload: { fraudCode: reasonCode } })
  })

  const reasonCode = watch('reasonCode')

  return (
    <Modal open={isMarkAsFraudModalOpened} onClose={toggleMarkAsFraudModal} aria-labelledby="mark-as-fraud-modal">
      {!markAsFraudStatus && (
        <ActionModal
          title={t('dialog.markDocAsFraud.title')}
          loading={isMarkAsFraudLoading}
          primaryButtonText={t('dialog.markDocAsFraud.submit')}
          primaryBtnTestId="mark-as-fraud-submit"
          disabled={isMarkAsFraudLoading || !reasonCode}
          secondaryButtonText={t('button.cancel')}
          handleClose={toggleMarkAsFraudModal}
          handleSubmit={handleMarkAsFraud}
        >
          <Flex direction="column" gap={16}>
            <Typography>{t('dialog.markDocAsFraud.description')}</Typography>
            <FormField label={t('dropdown.markDocAsFraud.title')}>
              <Select
                data-testid="mark-as-fraud-select"
                placeholder={t('dropdown.markDocAsFraud.placeholder')}
                defaultValue={
                  reasonCode
                    ? {
                        label: t(`dropdown.markDocAsFraud.${reasonCode}`),
                        value: reasonCode
                      }
                    : undefined
                }
                options={options}
                onChange={({ value }) => setValue('reasonCode', value as DocumentFraudCode)}
              />
            </FormField>
          </Flex>
        </ActionModal>
      )}
      {markAsFraudStatus === 'success' && (
        <AcknowledgeModal
          title={t('dialog.markDocAsFraud.success.title')}
          description={t('dialog.markDocAsFraud.success.description')}
          handleClose={toggleMarkAsFraudModal}
          result="positive"
          buttonText={t('button.close')}
        />
      )}
      {markAsFraudStatus === 'error' && (
        <AcknowledgeModal
          result="negative"
          title={t('dialog.somethingWentWrong')}
          handleClose={toggleMarkAsFraudModal}
          buttonText={t('button.close')}
          description={errorMessage}
        />
      )}
    </Modal>
  )
}
